import moment from 'moment'
import React, { useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import { useParams, useHistory } from 'react-router-dom'

import { EuiPage, EuiPageBody, EuiSpacer, EuiEmptyPrompt, EuiButton, EuiTabbedContent, EuiFlexItem, EuiLoadingChart, EuiFlexGroup } from '@elastic/eui'
import { EuiTabbedContentTab } from '@elastic/eui/src/components/tabs/tabbed_content/tabbed_content'

import { CampaignType, IntervalUnit } from 'api/interfaces'
import { useGetCampaignDetailReportQuery } from 'api/rtkQueryApi/platform/reportsApi'
import { RootState } from 'app/rootReducer'

import AdPerformance from './AdPerformance'
import CampaignCharts from './CampaignCharts'
import CampaignDetails from './CampaignDetails'
import CampaignPerformance from './CampaignPerformance'
import CampaignReportHeader from './CampaignReportHeader'
import CampaignMetricsPanels from './CampaignSummaryPanels'
import DomainPerformance from './DomainPerformance'
import GeoPerformance from './GeoPerformance'

const CampaignReportDetailPage: React.FC = () => {
  const { campaignId } = useParams<{ campaignId: string }>()
  const history = useHistory()
  const { currentAccount } = useSelector((state: RootState) => state.app)

  const [startDate, setStartDate] = useState<moment.Moment>(moment().subtract(30, 'days'))
  const [endDate, setEndDate] = useState<moment.Moment>(moment().subtract(1, 'days'))
  const [selectedGeoDimension, setSelectedGeoDimension] = useState<string>('City')

  const {
    data: detailedReport,
    isFetching,
    error
  } = useGetCampaignDetailReportQuery(
    {
      accountId: currentAccount?.id ?? '',
      campaignId: campaignId ?? '',
      start: startDate,
      end: endDate
    },
    { skip: !currentAccount?.id || !campaignId }
  )

  const campaign = detailedReport?.campaign
  const reportCampaign = detailedReport?.campaignPerformance
  const reportAdPerformanceGroups = detailedReport?.adPerformanceGroups
  const reportCampaignDomainDimension = detailedReport?.domainPerformance
  const reportCampaignGeoDimension = detailedReport?.geoPerformance?.[selectedGeoDimension.toLowerCase()]

  const isFetchingCampaign = isFetching
  const isLoadingReportCampaign = isFetching
  const isLoadingReportCampaignDomainDimension = isFetching
  const isLoadingReportCampaignGeoDimension = isFetching

  const campaignError = error

  const handleDateChange = (start: moment.Moment, end: moment.Moment) => {
    setStartDate(start)
    setEndDate(end)
  }

  const handleDateRangeSelect = (range: string) => {
    let newStart = moment()
    let newEnd = moment().subtract(1, 'days')

    switch (range) {
      case 'last_3':
        newStart = moment().subtract(3, 'days')
        break
      case 'last_7':
        newStart = moment().subtract(7, 'days')
        break
      case 'week':
        newStart = moment().startOf('week')
        newEnd = moment()
        break
      case 'last_30':
        newStart = moment().subtract(30, 'days')
        break
      case 'month':
        newStart = moment().startOf('month')
        newEnd = moment()
        break
      case 'last_month':
        newStart = moment().subtract(1, 'month').startOf('month')
        newEnd = moment().subtract(1, 'month').endOf('month')
        break
      case 'last_90':
        newStart = moment().subtract(90, 'days')
        break
      case 'custom':
        return
      default:
        newStart = moment().subtract(30, 'days')
    }
    setStartDate(newStart)
    setEndDate(newEnd)
  }

  const handleGeoDimensionChange = (dimension: string) => {
    setSelectedGeoDimension(dimension)
  }

  const handleBackClick = () => {
    history.push('/reports')
  }

  const tabs: EuiTabbedContentTab[] = [
    {
      id: 'campaign-data',
      name: 'Campaign Data',
      content: (
        <>
          <EuiSpacer size='m' />
          <CampaignPerformance results={reportCampaign} isLoading={isLoadingReportCampaign} />
        </>
      )
    },
    {
      id: 'ad-data',
      name: 'Ads',
      content: (
        <>
          <EuiSpacer size='m' />
          <AdPerformance adPerformanceGroups={reportAdPerformanceGroups || []} campaign={campaign} isLoading={isFetching} />
        </>
      )
    },
    ...(campaign?.type === CampaignType.Internet
      ? [
          {
            id: 'domain-data',
            name: 'Domains',
            content: (
              <>
                <EuiSpacer size='m' />
                <DomainPerformance placementData={reportCampaignDomainDimension || []} campaignData={reportCampaign || []} isLoading={isLoadingReportCampaignDomainDimension} />
              </>
            )
          }
        ]
      : []),
    ...(campaign?.type === CampaignType.Internet || campaign?.type === CampaignType.TV || campaign?.type === CampaignType.Audio
      ? [
          {
            id: 'geo-data',
            name: 'Geo Data',
            content: (
              <>
                <EuiSpacer size='m' />
                <GeoPerformance placementData={reportCampaignGeoDimension || []} campaignData={reportCampaign || []} isLoading={isLoadingReportCampaignGeoDimension} onDimensionChange={handleGeoDimensionChange} />
              </>
            )
          }
        ]
      : []),
    {
      id: 'charts',
      name: 'Charts',
      content: (
        <>
          <EuiSpacer size='m' />
          <CampaignCharts campaignData={reportCampaign || []} adPerformanceGroups={reportAdPerformanceGroups || []} campaign={campaign} isLoading={isLoadingReportCampaign} />
        </>
      )
    }
  ]

  if (isFetchingCampaign && !campaign) {
    return (
      <>
        <Helmet>
          <title>Campaign Report</title>
        </Helmet>

        <EuiPage className='euiPage--platform'>
          <EuiPageBody>
            <CampaignReportHeader accountName={currentAccount?.name} onBackClick={handleBackClick} />

            <EuiSpacer size='xxl' />

            <EuiFlexGroup justifyContent='spaceAround' alignItems='flexStart'>
              <EuiFlexItem grow={false}>
                <EuiLoadingChart size='xl' />
              </EuiFlexItem>
            </EuiFlexGroup>
          </EuiPageBody>
        </EuiPage>
      </>
    )
  }

  if (campaignError || !campaign) {
    return (
      <>
        <Helmet>
          <title>Campaign Report</title>
        </Helmet>

        <EuiPage className='euiPage--platform'>
          <EuiPageBody style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - var(--euiHeaderHeightCompensation))' }}>
            <div style={{ flex: '0 0 auto' }}>
              <CampaignReportHeader accountName={currentAccount?.name} onBackClick={handleBackClick} />

              <EuiSpacer size='xxl' />

              <EuiEmptyPrompt
                title={<h2>Campaign not found</h2>}
                body='The requested campaign could not be found.'
                actions={
                  <EuiButton onClick={handleBackClick} color='primary'>
                    Back to Reports
                  </EuiButton>
                }
              />
            </div>
          </EuiPageBody>
        </EuiPage>
      </>
    )
  }

  return (
    <React.Fragment>
      <Helmet>
        <title>Report - {campaign.name}</title>
      </Helmet>

      <EuiPage className='euiPage--platform'>
        <EuiPageBody style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - var(--euiHeaderHeightCompensation))' }}>
          <div style={{ flex: '0 0 auto' }}>
            <CampaignReportHeader accountName={currentAccount?.name} onBackClick={handleBackClick} />

            <EuiSpacer size='xxl' />

            <CampaignDetails campaign={campaign} />

            <EuiSpacer size='l' />

            <CampaignMetricsPanels results={reportCampaign} startDate={startDate} endDate={endDate} onDateChange={handleDateChange} onDateRangeSelect={handleDateRangeSelect} isLoading={isLoadingReportCampaign} />

            <EuiSpacer size='l' />
          </div>

          <div style={{ flex: '1 1 auto', minHeight: '0' }}>
            <EuiTabbedContent tabs={tabs} initialSelectedTab={tabs[0]} />
          </div>
        </EuiPageBody>
      </EuiPage>
    </React.Fragment>
  )
}

export default CampaignReportDetailPage
